import { z } from "zod";
import { CURRENCY_CODES } from "../money/currency.ts";
import { STORE_CAPABILITIES } from "../catalog/capabilities.ts";
import { TRADING_STATUSES } from "./operations.ts";
import { I18nTextSchema } from "./store.ts";

/** Contracts for `/api/chain/**` (`12-chain.md` §§ 2–3). */

export const SubstoreSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  isMain: z.boolean(),
  status: z.string(),
  address: z.string().nullable(),
  primaryCurrency: z.enum(CURRENCY_CODES),
  timezone: z.string(),
  tradingStatus: z.enum(TRADING_STATUSES),
  openTime: z.string(),
  closeTime: z.string(),
  itemCount: z.number().int(),
  tableCount: z.number().int(),
  staffCount: z.number().int(),
  capabilities: z.record(z.enum(STORE_CAPABILITIES), z.boolean()),
});
export type Substore = z.infer<typeof SubstoreSchema>;

export const CreateSubstoreSchema = z.object({
  name: z.string().min(1).max(120),
  slug: z
    .string()
    .min(2)
    .max(60)
    .regex(/^[a-z0-9][a-z0-9-]*$/, "lower kebab case"),
  address: z.string().nullable().default(null),
  phone: z.string().nullable().default(null),
  timezone: z.string().default("UTC"),
});

/**
 * Partial on purpose: HQ's console saves the switches it is showing, and a
 * full record would mean a client that had not been redeployed since a new
 * capability shipped would silently reset it to whatever it last knew.
 */
export const SaveCapabilitiesSchema = z.object({
  capabilities: z.partialRecord(z.enum(STORE_CAPABILITIES), z.boolean()),
});

/** Contracts for chain user administration (`12-chain.md` §§ 1, 3). */

export const MERCHANT_USER_SCOPES = ["chain", "stores"] as const;
export type MerchantUserScope = (typeof MERCHANT_USER_SCOPES)[number];

export const ChainUserSchema = z.object({
  id: z.uuid(),
  username: z.string(),
  displayName: z.string(),
  email: z.email().nullable(),
  status: z.enum(["active", "disabled"]),
  roleId: z.uuid().nullable(),
  roleName: z.string().nullable(),
  scope: z.enum(MERCHANT_USER_SCOPES),
  /** The stores a `stores`-scoped account reaches. Empty on a chain account. */
  storeIds: z.array(z.uuid()),
});
export type ChainUser = z.infer<typeof ChainUserSchema>;

/**
 * Saving access is one call, because the two halves are one decision: a
 * `stores` account with no stores reaches nothing and cannot sign in usefully,
 * so the scope and the list have to be validated against each other rather
 * than left to arrive in either order.
 */
export const SaveUserAccessSchema = z
  .object({
    scope: z.enum(MERCHANT_USER_SCOPES),
    storeIds: z.array(z.uuid()).default([]),
  })
  .refine((value) => value.scope === "chain" || value.storeIds.length > 0, {
    message: "Pick at least one store, or give this account the whole chain.",
    path: ["storeIds"],
  });
export type SaveUserAccess = z.infer<typeof SaveUserAccessSchema>;

/** Contracts for the head-office menu push (`12-chain.md` § 4a). */

export const ChainMenuItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  number: z.string().nullable(),
  /** Decimal string in the head office's currency, like every other price. */
  sellPrice: z.string(),
  status: z.string(),
  categoryNames: z.array(z.string()),
  /** How many stores already carry this item, pushed from here. */
  pushedToCount: z.number().int(),
  i18n: I18nTextSchema.optional(),
});
export type ChainMenuItem = z.infer<typeof ChainMenuItemSchema>;

export const PushMenuSchema = z.object({
  itemIds: z.array(z.uuid()).min(1).max(200),
  /** The stores to push to. Ignored when `groupId` names a part of the chain. */
  storeIds: z.array(z.uuid()).default([]),
  /**
   * Push to a named part of the chain instead — "the Southwest region".
   *
   * Resolves to the group's own stores plus its descendants', minus head
   * office, which is the source of a push and never a target.
   */
  groupId: z.uuid().optional(),
  /**
   * Overwrite rows a store has edited since they were pushed. Off by default:
   * the whole point of tracking the override is that head office has to say so.
   */
  overwriteLocal: z.boolean().default(false),
});
export type PushMenu = z.infer<typeof PushMenuSchema>;

export const PushMenuResultSchema = z.object({
  results: z.array(
    z.object({
      storeId: z.uuid(),
      storeName: z.string(),
      created: z.number().int(),
      updated: z.number().int(),
      skippedOverridden: z.number().int(),
    }),
  ),
});
export type PushMenuResult = z.infer<typeof PushMenuResultSchema>;

// ── Store groups ────────────────────────────────────────────────────────────

/**
 * A named part of a chain — a region, a district, a franchise cohort.
 *
 * Nested through `parentId`, and **reusable**: a store belongs to as many groups
 * as it needs to. The Southwest region, franchise-owned, and the airport
 * locations cut across each other, and a single containment tree could express
 * at most one of them.
 */
export const StoreGroupSchema = z.object({
  id: z.uuid(),
  parentId: z.uuid().nullable(),
  code: z.string(),
  name: z.string(),
  sort: z.number().int(),
  /** Directly attached stores. The full membership includes descendants'. */
  storeIds: z.array(z.uuid()),
  /** Everything this group means, descendants included. */
  reachesStoreIds: z.array(z.uuid()),
});
export type StoreGroup = z.infer<typeof StoreGroupSchema>;

export const SaveStoreGroupSchema = z.object({
  parentId: z.uuid().nullable().default(null),
  code: z.string().min(1).max(32),
  name: z.string().min(1).max(80),
  sort: z.number().int().min(0).default(0),
});

/** Written out rather than `.partial()`, for the reason `PatchJobSchema` gives. */
export const PatchStoreGroupSchema = z.object({
  parentId: z.uuid().nullable().optional(),
  code: z.string().min(1).max(32).optional(),
  name: z.string().min(1).max(80).optional(),
  sort: z.number().int().min(0).optional(),
});

/** Membership is replaced whole, the shape a recipe is edited in. */
export const SetStoreGroupStoresSchema = z.object({ storeIds: z.array(z.uuid()).max(500) });

// ── Franchise royalties ─────────────────────────────────────────────────────

export const ROYALTY_BASES = ["net_sales", "gross_sales"] as const;
export type RoyaltyBasisCode = (typeof ROYALTY_BASES)[number];

/**
 * The terms in a franchise agreement.
 *
 * Two rates, because they are two obligations with two destinations: a royalty
 * is the franchisor's revenue, a marketing fund contribution is held for
 * advertising and is often owed back to the franchisees collectively. Every
 * agreement states them separately, and a single combined percentage could not
 * be reported on separately either.
 */
export const RoyaltySchemeSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  royaltyRateBp: z.number().int(),
  marketingFundRateBp: z.number().int(),
  basis: z.enum(ROYALTY_BASES),
  isActive: z.boolean(),
  storeIds: z.array(z.uuid()),
});
export type RoyaltyScheme = z.infer<typeof RoyaltySchemeSchema>;

export const SaveRoyaltySchemeSchema = z.object({
  name: z.string().min(1).max(80),
  royaltyRateBp: z.number().int().min(0).max(10_000).default(0),
  marketingFundRateBp: z.number().int().min(0).max(10_000).default(0),
  basis: z.enum(ROYALTY_BASES).default("net_sales"),
  isActive: z.boolean().default(true),
});

/** Written out rather than `.partial()`, for the reason `PatchJobSchema` gives. */
export const PatchRoyaltySchemeSchema = z.object({
  name: z.string().min(1).max(80).optional(),
  royaltyRateBp: z.number().int().min(0).max(10_000).optional(),
  marketingFundRateBp: z.number().int().min(0).max(10_000).optional(),
  basis: z.enum(ROYALTY_BASES).optional(),
  isActive: z.boolean().optional(),
});

export const SetRoyaltySchemeStoresSchema = z.object({
  storeIds: z.array(z.uuid()).max(500),
});

export const RunRoyaltySchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD"),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD"),
});

export const RoyaltyChargeSchema = z.object({
  id: z.uuid(),
  storeId: z.uuid(),
  storeName: z.string(),
  schemeId: z.uuid(),
  schemeName: z.string(),
  periodStart: z.string(),
  periodEnd: z.string(),
  basisAmount: z.string(),
  royaltyAmount: z.string(),
  marketingAmount: z.string(),
  royaltyRateBpSnapshot: z.number().int(),
  marketingFundRateBpSnapshot: z.number().int(),
  basisSnapshot: z.enum(ROYALTY_BASES),
  currency: z.string(),
  status: z.enum(["draft", "final"]),
  finalisedAt: z.string().nullable(),
});
export type RoyaltyCharge = z.infer<typeof RoyaltyChargeSchema>;
